import {
  map,
  cond,
  always,
  T,
  replace,
  clone,
} from 'ramda';

import {
  getFeaturesSegment,
  getBodyStyle,
  getColorSegment,
  getTrimsSegment,
  getOptionsSegment,
  getYearsSegment,
  getSegmentForNoModelSelect,
  getSegmentForAllModelSelect,
  getSegmentForMultiMakeModel,
  getSegmentForSingleModelParentMakeSelect,
  getSegmentForSingleModelSelect,
  getLocationUrlSegment,
  postProcessing,
  preProcessingUrls
} from './Urls/Rules';

import {
  postProcessingCrumbs,
  getCrumbForSingleModelParentMakeSelect,
  getCrumbForSingleModelSelect,
  getCrumbForAllModelSelect,
  getCrumbForNoModelSelect,
  getCrumbForMultiMakeModel,
  getBodyStyleCrumb,
  getYearsCrumb,
  getColorCrumb,
  getTrimsCrumb,
  getFeaturesCrumb,
  getOptionsCrumb,
  getLocationCrumb,
  preProcessingCrumbs
} from './Breadcrumbs/CrumbRules';

import {
  getColorMetadataSegment,
  getFeaturesMetadataSegment,
  getMetadataSegmentForAllModelSelect,
  getMetadataSegmentForMultiMakeModel,
  getMetadataSegmentForNoModelSelect,
  getMetadataSegmentForSingleModelParentMakeSelect,
  getMetadataSegmentForSingleModelSelect,
  getOptionsMetadataSegment,
  getYearsMetadataSegment,
  getBodyStyleMetadataSegment,
  preProcessingMetadata,
  postProcessingMetadata,
  getTrimMetadataSegment,
  getLocationMetadataSegment
} from './Metadata/MetadataRules';

import { RulesState } from './RulesState';
import { bodyStylesCoupe } from '../__mock__/AppliedFilters.mock';

const operationTypes = {
  modelIds: 'm',
  terms: 't',
  options: 'o',
  location: 'l',
  ranges: 'r',
  features: 'f'
};

// predicates
const matchModelIdsPredicate = () => ({ data, operation }) => (
  operation === operationTypes.modelIds && data && Object.keys(data) && data.models && data.models.length
);

const hasAnyOptions = options => Object.keys(options).reduce((p,c)=>p + options[c].terms.length, 0);

const matchFeaturesPredicate = () => ({ data, operation }) => (operation === operationTypes.features &&
  data && ((data.features.terms && data.features.terms.length) || Object.keys(data.isFreeDelivery).length));

const matchStandardPredicate = () => ({ data, operation }) => (
  (operation === operationTypes.terms && data && data.terms && data.terms.length) ||
  (operation === operationTypes.ranges && data && data.ranges && data.ranges.length) ||
  (operation === operationTypes.options && hasAnyOptions(data)) ||
  (operation === operationTypes.location && data && Object.keys(data).length)
);

// actions
const matchHasFeaturesAction = rulesState => ({data, callback}) => callback({data: data.features, rulesState});
const standardAction = rulesState => ({data, callback}) => callback({data, rulesState});
const matchModelIdsAction = rulesState => ({data, callback}) => (
  Object.keys(callback)
    .map(func => callback[func]({modelIds: data, rulesState}))
    .filter(Boolean)
);


// if type of operation meets standard type then do standard action callback pattern
// else if type of operation is modelIds, then we must call multiple callbacks
// else if type is of operation we have special case for features to include free delivery
const patternMatch = rulesState =>
  cond([
    [matchStandardPredicate(), standardAction(rulesState)],
    [matchModelIdsPredicate(), matchModelIdsAction(rulesState)],
    [matchFeaturesPredicate(), matchHasFeaturesAction(rulesState)],
    [T, always('')]
  ]);


const traverse = (operations, rulesState) => map(patternMatch(rulesState), operations);

// use a fast check to avoid cloning if not required
const shouldUseSafeSelectedFilters = selectedFilters => selectedFilters?.bodyStyles?.terms?.length;

// clone for immutability any props we plan to modify in situ
// so that we don't modify upward the data contract back to the client calling traffic
const maybeSafeFilters = selectedFilters => {
  if (!shouldUseSafeSelectedFilters(selectedFilters))
    return selectedFilters

  const {
    bodyStyles: rawBodyStyles,
  } = selectedFilters;

  // selectively/smartly clone props we want to modify for ease of calculation
  // cloning will protect the upstream client but incurs performance hit
  // e.g. 3% hit on bodystyles but up to 40% to clone all filters on every call
  // need to be selective on this approach using shouldUseSafeSelectedFilters
  const bodyStyles = clone(rawBodyStyles);

  // no matter which type the engine is running we want Truck instead of Pickup
  bodyStyles.terms.forEach(x=> {x.term = replace('Pickup', 'Truck', x.term)});

  // return a new object with original properties and cloned props to replace original where desired
  return {
    ...selectedFilters,
    bodyStyles
  };
}


const runEngine = rulesConfig => selectedFilters => location =>
  getSingleModelParentMake => getSingleModel => getMultiModel => getAllModels => getNoModels =>
  getBodyStyleSegment => getColor => getTrims => getYear => getFeatures => getOptions => getLocation =>
  preProcessingStep => postProcessingStep => {

  // rules share a state object to communicate
  const rulesState = new RulesState(rulesConfig);

  const safeFilters = maybeSafeFilters(selectedFilters);

  const {
    bodyStyles,
    trims,
    year,
    colors,
    features,
    isFreeDelivery,
  } = safeFilters;

  preProcessingStep({selectedFilters: safeFilters, rulesState});

  const {
    driveTypes,
    transmissions,
    cylinderCounts,
    fuelTypes,
    modelIds,
  } = selectedFilters;

  const operations = [
    {
      operation: operationTypes.modelIds,
      data: modelIds,
      callback: { getAllModels, getNoModels, getSingleModelParentMake, getSingleModel, getMultiModel }
    },
    {
      operation: operationTypes.terms,
      data: bodyStyles,
      callback: getBodyStyleSegment
    },
    {
      operation: operationTypes.terms,
      data: trims,
      callback: getTrims
    },
    {
      operation: operationTypes.ranges,
      data: year,
      callback: getYear
    },
    {
      operation: operationTypes.terms,
      data: colors,
      callback: getColor
    },
    {
      operation: operationTypes.features,
      data: {features, isFreeDelivery},
      callback: getFeatures
    },
    {
      operation: operationTypes.options,
      data: {
        transmissions, fuelTypes, cylinderCounts, driveTypes
      },
      callback: getOptions
    },
    {
      operation: operationTypes.location,
      data:location,
      callback: getLocation
    }
  ];

  return postProcessingStep({
      results: [...traverse(operations, rulesState)],
      rulesState,
      selectedFilters: safeFilters
    })
  ;
};

export const getUrl = (selectedFilters, selectedFiltersForApi, location={}) => {
  return new Promise(resolve => {
    resolve(
      runEngine({selectedFiltersForApi})
        (selectedFilters)
        (location)
        (getSegmentForSingleModelParentMakeSelect)
        (getSegmentForSingleModelSelect)
        (getSegmentForMultiMakeModel)
        (getSegmentForAllModelSelect)
        (getSegmentForNoModelSelect)
        (getBodyStyle)
        (getColorSegment)
        (getTrimsSegment)
        (getYearsSegment)
        (getFeaturesSegment)
        (getOptionsSegment)
        (getLocationUrlSegment)
        (preProcessingUrls)
        (postProcessing)
    );
  });

};

export const getUrlCars = (selectedFilters, selectedFiltersForApi, location={}) => {

  return new Promise(resolve => {
    resolve(
      {
        segments: [],
        pathname: '',
        flags: {
          ...RulesState.InitialFlags,
          isNoMake: true, isNoModels: true
        },
        cvnaid: undefined,
        search: ''
    }
    );
  });

};

export const getUrlSync = (selectedFilters, selectedFiltersForApi, location={}) => {
  return runEngine({selectedFiltersForApi})
      (selectedFilters)
      (location)
      (getSegmentForSingleModelParentMakeSelect)
      (getSegmentForSingleModelSelect)
      (getSegmentForMultiMakeModel)
      (getSegmentForAllModelSelect)
      (getSegmentForNoModelSelect)
      (getBodyStyle)
      (getColorSegment)
      (getTrimsSegment)
      (getYearsSegment)
      (getFeaturesSegment)
      (getOptionsSegment)
      (getLocationUrlSegment)
      (preProcessingUrls)
      (postProcessing)
};

export const getUrlCarsSync = (selectedFilters, selectedFiltersForApi, location={}) => {
  return       {
    segments: [],
    pathname: '',
    flags: {
      ...RulesState.InitialFlags,
      isNoMake: true, isNoModels: true
    },
    cvnaid: undefined,
    search: ''
}
};
export const getBreadcrumbs = (selectedFilters, selectedFiltersForApi, allowCvnaid = true, location={}) => {
  return new Promise(resolve => {
    resolve(
      runEngine({selectedFiltersForApi, allowCvnaid})
        (selectedFilters)
        (location)
        (getCrumbForSingleModelParentMakeSelect)
        (getCrumbForSingleModelSelect)
        (getCrumbForMultiMakeModel)
        (getCrumbForAllModelSelect)
        (getCrumbForNoModelSelect)
        (getBodyStyleCrumb)
        (getColorCrumb)
        (getTrimsCrumb)
        (getYearsCrumb)
        (getFeaturesCrumb)
        (getOptionsCrumb)
        (getLocationCrumb)
        (preProcessingCrumbs)
        (postProcessingCrumbs)
    );
  });
};

export const getMetadata = (selectedFilters, location={}) => {
  return runEngine({})
    (selectedFilters)
    (location)
    (getMetadataSegmentForSingleModelParentMakeSelect)
    (getMetadataSegmentForSingleModelSelect)
    (getMetadataSegmentForMultiMakeModel)
    (getMetadataSegmentForAllModelSelect)
    (getMetadataSegmentForNoModelSelect)
    (getBodyStyleMetadataSegment)
    (getColorMetadataSegment)
    (getTrimMetadataSegment)
    (getYearsMetadataSegment)
    (getFeaturesMetadataSegment)
    (getOptionsMetadataSegment)
    (getLocationMetadataSegment)
    (preProcessingMetadata)
    (postProcessingMetadata)
};

export default getUrl;
